import React, { Component } from "react";
import PropTypes from "prop-types";
import * as constants from "../../../constants";
import { searchLocation, fetchAdvertiserTypes, searchAdvertisers } from "../../../api";
import RightPanel from "../../common/RightPanel";
import Form from "../../common/Form";
import { debounce } from "lodash";

export default class EditAdvertiser extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasEditError: false,
      advertiser: this.props.advertiser,
      duplicateClients: [],
      confirmedIgnoreDuplicate: false,
    };
  }
  static propTypes = {
    isOpen: PropTypes.bool.isRequired,
    advertiser: PropTypes.object,
    onUpdate: PropTypes.func.isRequired,
  };

  componentDidUpdate(prevProps) {
    if (this.props.advertiser !== prevProps.advertiser) {
      this.setState({
        advertiser: this.props.advertiser,
        duplicateClients: [],
        confirmedIgnoreDuplicate: false,
      });
      this.lastNameQuery = "";
    }
  }

  componentWillUnmount() {
    this.debouncedDuplicateSearch.cancel();
  }

  cancelUpdate = () => {
    this.setState({ hasEditError: false, duplicateClients: [], confirmedIgnoreDuplicate: false });
    this.lastNameQuery = "";
    this.props.cancelEdit();
  };

  isValid = (advertiser) => {
    if (
      advertiser === undefined ||
      !advertiser.name ||
      !advertiser.cityId ||
      !advertiser.advertiserTypeId
    )
      return false;
    else return true;
  };

  handlePrimaryButtonClick = () => {
    const { advertiser, duplicateClients } = this.state;
    if (advertiser?.id) {
      this.update();
      return;
    }

    if (duplicateClients.length > 0) {
      this.setState({ confirmedIgnoreDuplicate: true }, () => {
        this.update();
      });
    } else {
      this.update();
    }
  };

  update = () => {
    const { advertiser, confirmedIgnoreDuplicate } = this.state;
    if (this.isValid(advertiser)) {
      const ignoreDuplicate = !advertiser.id && confirmedIgnoreDuplicate;
      this.props.onUpdate({
        ...advertiser,
        ignoreDuplicate,
      });
      this.cancelUpdate();
    } else {
      return this.setState({ hasEditError: true });
    }
  };

  debouncedDuplicateSearch = debounce(async (value, advertiser) => {
    try {
      const { data } = await searchAdvertisers(value);
      const results = Array.isArray(data) ? data : [];

      if (value !== this.lastNameQuery) return;

      this.setState({
        duplicateClients: advertiser?.id ? results.filter((c) => c.id !== advertiser.id) : results,
        confirmedIgnoreDuplicate: false,
      });
    } catch {
      if (value !== this.lastNameQuery) return;
      this.setState({
        duplicateClients: [],
        confirmedIgnoreDuplicate: false,
      });
    }
  }, 400);

  inputOnChange = async (value, accessor) => {
    const { advertiser } = this.state;
    this.setState({
      advertiser: { ...advertiser, [accessor]: value },
    });
    if (accessor === "name") {
      this.lastNameQuery = value || "";

      if (value?.length > 2) {
        this.debouncedDuplicateSearch(value, advertiser);
      } else {
        this.setState({
          duplicateClients: [],
          confirmedIgnoreDuplicate: false,
        });
      }
    }
  };

  render = () => {
    const { isOpen } = this.props;
    const { advertiser = {}, hasEditError } = this.state;
    return (
      <RightPanel
        isOpen={isOpen}
        onClose={() => this.cancelUpdate()}
        header={advertiser.id ? "RightPanelHeader.updateClient" : "RightPanelHeader.addClient"}
        showFooter={true}
        isEdit={advertiser.id ? true : false}
        primaryButtonProps={{
          text: this.state.duplicateClients.length > 0 ? "Button.IgnoreAdd" : "Button.add",
          onClick: this.handlePrimaryButtonClick,
          disabled: !this.isValid(advertiser),
        }}
        secondaryButtonProps={{ text: "Button.cancel", onClick: this.cancelUpdate }}
      >
        {this.state.duplicateClients.length > 0 && (
          <div
            style={{
              background: "#f5f6f8",
              borderRadius: "6px",
              padding: "12px 14px",
              marginBottom: "12px",
              position: "relative",
            }}
          >
            {/* Left orange bar */}
            <div
              style={{
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: "4px",
                background: "#f5a623",
                borderTopLeftRadius: "6px",
                borderBottomLeftRadius: "6px",
              }}
            />

            {/* Header row */}
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
              {/* Warning circle */}
              <div
                style={{
                  width: "22px",
                  height: "22px",
                  borderRadius: "50%",
                  background: "#f5a623",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                  fontWeight: "bold",
                  fontSize: "14px",
                }}
              >
                !
              </div>

              {/* Text */}
              <div style={{ fontSize: "14px" }}>
                Found <b>{Math.min(this.state.duplicateClients.length, 5)}</b>
                {this.state.duplicateClients.length > 5 &&
                  ` of ${this.state.duplicateClients.length}`}{" "}
                Clients similar to <b>{this.state.advertiser?.name}</b>
              </div>
            </div>

            {/* List */}
            <div style={{ paddingLeft: "30px" }}>
              {this.state.duplicateClients.slice(0, 5).map((c, i) => (
                <div key={c.id} style={{ marginBottom: "4px" }}>
                  {i + 1}. <span style={{ color: "#1f4f82", fontWeight: 500 }}>{c.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        <Form
          config={[
            {
              id: "name",
              type: constants.FORM_FIELD_TYPE.INPUT,
              label: "Client Name",
              className: "col-12",
              placeholder: "Enter Client Name",
              value: advertiser.name,
              error: hasEditError && !advertiser?.name,
              errorMessage: "Enter Client Name",
              onChange: (e) => this.inputOnChange(e.target.value, "name"),
            },
            {
              id: "advertisertype",
              type: constants.FORM_FIELD_TYPE.API_SELECT,
              label: "Client Type",
              className: "col-12",
              placeholder: "Select Client Type",
              value: advertiser.advertiserTypeId,
              fetchAction: () => fetchAdvertiserTypes("rank"),
              parseResults: (response) => {
                return [
                  ...response.map((item) => ({
                    id: item.id,
                    value: item.name,
                  })),
                ];
              },
              selected: (list) => list.filter((item) => item.id === advertiser.advertiserTypeId),
              singleSelect: true,
              showLabelInButton: false,
              onSelect: ([data]) => {
                this.inputOnChange(data.id, "advertiserTypeId");
              },
              error: hasEditError && !advertiser?.advertiserTypeId,
              errorMessage: "Select Client Type",
            },
            {
              id: "cityId",
              type: constants.FORM_FIELD_TYPE.API_SEARCH,
              label: "Location",
              placeholder: "Select Location",
              onSelect: (props) => {
                this.inputOnChange(props.id, "cityId");
              },
              searchIcon: "right",
              query: advertiser.cityName,
              showSubText: true,
              parseResults: (data) => {
                return data.results.slice(0, 15).map((item) => ({
                  id: item.id,
                  name: item.name,
                  tag: `${item.province_name} · ${item.country_name}`,
                }));
              },
              fetchAction: (value) => searchLocation(value, null, "city"),
              error: hasEditError && !advertiser?.cityId,
              errorMessage: "Select location",
            },
          ]}
        />
      </RightPanel>
    );
  };
}
